import { Checkbox, createStyles, ScrollArea, Table } from "@mantine/core";
import { ReactNode, useState } from "react";
import theme from "../stores/theme";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "./icons";
import { faArrowsAlt } from "@fortawesome/pro-duotone-svg-icons";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { getSortedData } from "../components/app-container-components/configure-formula";

export interface TableViewProps {
  actions?: (row: { [key: string]: string | null | number }) => ReactNode;
  data: TableData[];
  columns: { label: string; key: string; dataAlign?: string; width?: string | number }[];
  height?: string | number;
  order?: number[];
  reOrder?: (order: number[]) => void;
  groupBy?: string;
  headerAlign?: { [key: string]: string };
  selectRows?: (row: { [key: string]: string | null | number }) => ReactNode;
  onSelectAll?: (checked: boolean) => void;
  allSelected?: boolean;
}
export type TableData = {
  [key: string]: string | null | number;
};
export const useTableStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor: theme.other.table.header.backgroundColor,
    transition: "box-shadow 150ms ease",
    zIndex: 1,
    "&::after": {
      content: '""',
      position: "absolute",
      borderBottom: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.gray[2]}`,
    },
  },
  scrolled: {
    boxShadow: theme.other.arc.shadows.sm,
  },
}));

const RowDragger = (props: { id: string; row: number; children?: React.ReactNode; isDragging?: boolean; isSelected: boolean }) => {
  const { setNodeRef, attributes, transform, listeners, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <tr
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: props.isDragging && props.isSelected ? "#f6f6f6" : "#fff",
      }}
    >
      <td
        key={`${props.row}}|${-1}`}
        {...attributes}
        {...listeners}
        style={{
          cursor: props.isDragging && props.isSelected ? "grabbing" : "grab",
          width: 50,
        }}
      >
        {props.id ? <Icon icon={faArrowsAlt} /> : <></>}
      </td>
      {props.children}
    </tr>
  );
};

const isNumeric = (value: string | null | number | undefined): boolean => {
  if (value === null || value === undefined || value === "") return false;

  const stringValue = value.toString().trim();
  if (/[^0-9.,-]/.test(stringValue)) return false;
  const numericString = stringValue.replace(/,/g, "");

  return !isNaN(Number(numericString));
};

const isColumnNumeric = (data: TableData[], key: string): boolean => {
  if (!data || data.length === 0) return false;

  const sampleSize = Math.min(10, data.length);
  let numericCount = 0;

  for (let i = 0; i < sampleSize; i++) {
    if (data[i] && data[i][key] !== undefined && data[i][key] !== null) {
      if (isNumeric(data[i][key])) {
        numericCount++;
      } else if (data[i][key] !== "") {
        return false;
      }
    }
  }

  return numericCount >= sampleSize * 0.8;
};

export const TableView = (props: TableViewProps) => {
  const { classes, cx } = useTableStyles();
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState<string>("");
  const sensors = useSensors(
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );
  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id.toString());
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const orderedData = props.data.map((d) => d["id"]);
    if (event.active == null || event.over == null || event.active.id === event.over.id) {
      return;
    }
    if (event.active.id !== event.over.id) {
      const oldIndex = orderedData.indexOf(event.active.id);
      const newIndex = orderedData.indexOf(event.over.id);
      if (props.reOrder == null) return;
      const newOrder = arrayMove(orderedData, oldIndex, newIndex)
        .filter((data) => data != null)
        .map((od) => parseInt(od?.toString() ?? "0"));
      props?.reOrder(newOrder);
    }
    setActiveId("");
  };

  // Determine which columns are numeric
  const numericColumns = props.columns.reduce((acc, column) => {
    acc[column.key] = isColumnNumeric(props.data, column.key);
    return acc;
  }, {} as Record<string, boolean>);

  const headers = props.selectRows
    ? [
        <Checkbox
          style={{ marginLeft: "2.5px" }}
          key="select-all"
          checked={props.allSelected || false}
          onChange={(e) => props.onSelectAll?.(e.currentTarget.checked)}
        />,
        ...props.columns.map((c) => c.label),
      ]
    : props.columns.map((c) => c.label);

  if (props.actions) {
    if (props.actions.length > 0) {
      headers.push("Actions");
    }
  }

  const renderRows = () => {
    return props.data.map((r, i) => {
      const rowElements: ReactNode[] = [];
      if (props.selectRows) {
        rowElements.push(
          <td width={60} key={`select${i}`}>
            {props.selectRows(r)}
          </td>
        );
      }
      props.columns.forEach((c, j) => {
        const value = r[c.key];
        const align = numericColumns[c.key] ? "right" : "left";
        rowElements.push(
          <td key={i + j} style={{ textAlign: align, width: c.width }}>
            {value}
          </td>
        );
      });
      if (props.actions) {
        rowElements.push(<td key={i + 3}>{<div>{props.actions(r)}</div>}</td>);
      }
      return <tr key={i}>{rowElements}</tr>;
    });
  };

  const dragableTableView = () => {
    const orderedData = (props.order ?? []).map((d) => d.toString());
    const data = orderedData.length > 0 ? (getSortedData(orderedData, props.data) as TableData[]) : props.data;
    const selected = props.data.find((d) => d["id"] == activeId);
    return (
      <Table verticalSpacing="xs" withBorder withColumnBorders>
        <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
          <tr>
            <th>
              <Icon icon={faArrowsAlt} />
            </th>
            {props.columns.map((c, i) => {
              const align = numericColumns[c.key] ? "right" : "left";
              return (
                <th key={i} style={{ textAlign: align }}>
                  {c.label}
                </th>
              );
            })}
            {props.actions && props.actions.length > 0 && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <SortableContext items={data.map((d) => d["id"]) as string[]} strategy={verticalListSortingStrategy}>
              {data.map((row, rowIndex) => {
                return (
                  <RowDragger
                    key={row["id"]}
                    id={row["id"]?.toString() ?? ""}
                    row={rowIndex + 2}
                    isDragging={activeId ? true : false}
                    isSelected={selected ? selected["id"] == row["id"] : false}
                  >
                    {props.columns.map((c, columnIndex) => {
                      const value = row[c.key];
                      const align = numericColumns[c.key] ? "right" : "left";
                      return (
                        <td key={`${rowIndex}}|${columnIndex}`} style={{ textAlign: align, width: c.width }}>
                          {value}
                        </td>
                      );
                    })}
                    <td key={rowIndex + 3}>{<div>{props.actions ? props.actions(row) : null}</div>}</td>
                  </RowDragger>
                );
              })}
            </SortableContext>
          </DndContext>
        </tbody>
      </Table>
    );
  };
  const getGroupedData = () => {
    const groupedData = props.data.reduce((tableData: { [key: string]: TableData[] }, item) => {
      const key = props.groupBy ? (item[props.groupBy] as string) : "";
      if (!tableData[key]) {
        tableData[key] = [];
      }
      tableData[key].push(item);
      return tableData;
    }, {});
    return groupedData;
  };

  const groupedTableRows = () => {
    const groupedData = getGroupedData();
    return (
      <>
        {Object.keys(groupedData).map((group, i) => (
          <>
            <tr key={i}>
              <td key={group} rowSpan={groupedData[group].length + 1} style={{ borderRight: "1px solid #dee2e6" }}>
                {group === "fixedRow" ? "" : group}
              </td>
            </tr>
            {groupedData[group].map((data, j) => (
              <tr key={j + group.length}>
                {props.columns.map((column, k) => {
                  if (column.key != "group") {
                    const value = data[column.key];
                    const align = numericColumns[column.key] ? "right" : "left";
                    return (
                      <td key={`${k} + ${column}`} style={{ textAlign: align }}>
                        {value}
                      </td>
                    );
                  }
                })}
              </tr>
            ))}
          </>
        ))}
      </>
    );
  };

  return (
    <ScrollArea style={{ height: props.height ? props.height : theme.other?.table.height }} onScrollPositionChange={({ y }) => setScrolled(y !== 0)}>
      {props.reOrder ? (
        dragableTableView()
      ) : (
        <Table verticalSpacing="xs" withBorder withColumnBorders>
          <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
            <tr>
              {props.selectRows && (
                <th style={{ textAlign: "left" }}>
                  <Checkbox
                    style={{ marginLeft: "2.5px" }}
                    key="select-all"
                    checked={props.allSelected || false}
                    onChange={(e) => props.onSelectAll?.(e.currentTarget.checked)}
                  />
                </th>
              )}
              {props.columns.map((c, index) => {
                const align = numericColumns[c.key] ? "right" : "left";
                return (
                  <th
                    style={{
                      textAlign: (props.headerAlign?.[`header-${index}`] as "center" | "left" | "right") || align,
                    }}
                    key={`header-${index}`}
                  >
                    {c.label}
                  </th>
                );
              })}
              {props.actions && props.actions.length > 0 && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>{props.groupBy ? groupedTableRows() : renderRows()}</tbody>
        </Table>
      )}
    </ScrollArea>
  );
};
